import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { RejectReason } from '../types'

/** "Diğer" sistem gerekçesi — silinemez/pasifleştirilemez/düzenlenemez/taşınamaz (ret akışı buna bağlı). */
const OTHER_ID = -1

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen (sıralı) satırlar — okların komşu-koruma hesabı için. */
  rows: RejectReason[]
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: RejectReason, dir: 'up' | 'down') => void
  onEdit: (row: RejectReason) => void
  onToggle: (row: RejectReason, active: boolean) => void
  onDelete: (row: RejectReason) => void
}

/**
 * Ret gerekçesi tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu yan yana
 * ↑/↓ okları (ortak SortSpinner). Durum satır-içi InputSwitch (Aktif/Pasif) + satır aksiyonları (düzenle/sil).
 * "Diğer" (-1) satırında tüm değiştiriciler pasiftir; satır yerinde sabittir (kendi + komşu okları kilitli).
 * (011 TicketTypeColumns ile birebir desen.)
 */
export function rejectReasonColumns({
  t,
  localized,
  busyId,
  rows,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  const rowCount = rows.length
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('admin.rejectReasons.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: RejectReason, { rowIndex }) => {
        const isProtected = row.id === OTHER_ID
        return (
          <SortSpinner
            onUp={() => onMove(row, 'up')}
            onDown={() => onMove(row, 'down')}
            disableUp={rowIndex === 0 || isProtected || rows[rowIndex - 1]?.id === OTHER_ID}
            disableDown={
              rowIndex === rowCount - 1 || isProtected || rows[rowIndex + 1]?.id === OTHER_ID
            }
            busy={reordering}
            labelUp={t('common.reorder.up')}
            labelDown={t('common.reorder.down')}
          />
        )
      }}
    />,
    <Column
      key="name"
      header={t('admin.rejectReasons.columns.name')}
      body={(row: RejectReason) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('admin.rejectReasons.columns.status')}
      style={{ width: '8rem' }}
      body={(row: RejectReason) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active ? t('admin.rejectReasons.status.active') : t('admin.rejectReasons.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.rejectReasons.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: RejectReason) => {
        const active = row.recordStatus === 1
        const protectedRow = row.id === OTHER_ID
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={protectedRow || busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('admin.rejectReasons.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('admin.rejectReasons.actions.edit')}
              tooltip={t('admin.rejectReasons.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={protectedRow}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('admin.rejectReasons.actions.delete')}
              tooltip={t('admin.rejectReasons.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={protectedRow || busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
